/**
 * Cliente de la API del backoffice — mismo origen (`OD-0026 D-06`).
 *
 * Esta aplicación se sirve desde el subdominio interno, y su API vive en
 * `/api/**` de **ese mismo origen**: es lo que hace funcionar la protección
 * CSRF (`Origin` exacto y `Sec-Fetch-Site: same-origin`). Por eso no hay
 * ninguna URL de API configurable — apuntar al host del producto rompería esa
 * protección y además compartiría cookie con la aplicación del cliente, que es
 * justo lo que separa el subdominio propio (`OD-0020` D-01).
 *
 * La cookie de sesión es `__Host-...`, `HttpOnly` y `SameSite=Strict`: este
 * código nunca la lee ni la escribe.
 *
 * Deliberadamente pequeño: sólo las rutas que el backoffice necesita. El
 * Worker del subdominio interno además sólo deja pasar esas (ver
 * `apps/admin/worker/index.ts`), así que añadir aquí una llamada al producto no
 * funcionaría — y eso es lo que se quiere.
 */

export type ErrorApi = {
  readonly code: string;
  readonly message: string;
  /** Motivo legible por máquina (p. ej. `MFA_REQUIRED`). */
  readonly motivo: string | null;
  readonly requestId: string;
};

export type Resultado<T> =
  | { readonly ok: true; readonly datos: T }
  /** `estado` 0 = la petición no salió (sin red). */
  | { readonly ok: false; readonly estado: number; readonly error: ErrorApi | null };

export type RespuestaLogin =
  | { readonly tipo: "AUTENTICADO" }
  | { readonly tipo: "MFA"; readonly challenge: string };

export type EmpresaResumen = {
  readonly id: string;
  readonly name: string;
  readonly planVersion: string;
  readonly status: string;
  readonly createdAt: string;
  readonly shard: string | null;
  /** Estado de la suscripción en Stripe. `null` = no hay, lo normal en Free. */
  readonly subscriptionState: string | null;
  /** No se pudo leer la suscripción. NO es lo mismo que no tenerla. */
  readonly subscriptionUnknown: boolean;
  /** Paga un plan y su suscripción no está viva. Nada lo baja solo (GATE-04). */
  readonly planSinSuscripcionViva: boolean;
  /**
   * `OD-0064` D-12: la prueba de Business de 30 días, si la tuvo. `endsAt` es
   * la medianoche de Madrid en que baja a Free. `null`: no tuvo, o una API
   * anterior que no lo manda.
   */
  readonly prueba: { readonly estado: EstadoDePrueba; readonly endsAt: string } | null;
};

export type EstadoDePrueba = "EN_CURSO" | "CONVERTIDA" | "TERMINADA";

const ESTADOS_DE_PRUEBA: readonly EstadoDePrueba[] = ["EN_CURSO", "CONVERTIDA", "TERMINADA"];

export type EmpresaFicha = EmpresaResumen & {
  readonly issuer: { readonly legalName: string; readonly taxId: string; readonly confirmed: boolean } | null;
  readonly representation: string;
  readonly remision: { readonly pending: number; readonly rejected: number; readonly incident: boolean } | null;
  /** Lo que la AEAT no dio por bueno: es lo que se subsana (Parte 4 §4). */
  /** Los últimos envíos: lo que hace accionable una incidencia del art. 16.4. */
  readonly submissions: readonly Envio[];
  readonly defects: readonly RegistroDefectuoso[];
  /** `OD-0044` D-04: archivos del cliente que no cuadran. */
  readonly archivosMarcados: readonly ArchivoMarcado[];
};

/**
 * Un archivo del cliente que no cuadra — `OD-0044` D-04.
 *
 * Llega el nombre que puso el usuario porque sin él no se le puede ni decir
 * cuál es. **No llega nada más**: ni bytes, ni huellas, ni claves de objeto. El
 * operador necesita saber qué pasa, no tener el contenido.
 */
export type ArchivoMarcado = {
  readonly fileId: string;
  readonly fileType: string;
  readonly originalName: string;
  readonly estado: string;
  readonly verifiedAt: string | null;
};

/**
 * El resultado de un repaso — `OD-0044` D-06.
 *
 * `sinDeterminar` va aparte de `marcados` a propósito: un timeout no es un
 * archivo corrupto, y sumarlos haría que una caída del almacén pareciera una
 * corrupción. `habiaAlgo: false` es «no había nada que comprobar», que NO es
 * «todo correcto».
 */
export type ResultadoRepaso = {
  readonly verificados: number;
  readonly marcados: number;
  readonly sinDeterminar: number;
  readonly habiaAlgo: boolean;
};

export type RegistroDefectuoso = {
  readonly recordId: string;
  readonly numSerie: string;
  readonly fechaExpedicion: string;
  readonly status: string;
  readonly aeatState: string | null;
  readonly errorCode: string | null;
  readonly errorDescription: string | null;
  readonly isSubsanation: boolean;
};

export type MotivoRechazo =
  | "NOT_FOUND"
  | "GRANTOR_MISMATCH"
  | "WRONG_PROCEDURE"
  | "WRONG_REPRESENTATIVE"
  | "NOT_IN_FORCE"
  | "OTHER";

/** Lo que devuelve «Enviar ahora»: las mismas cuentas que da el cron. */
export type ResultadoEnvio = {
  readonly sent: number;
  readonly accepted: number;
  readonly rejected: number;
  readonly waiting: number;
  readonly failed: boolean;
};

function comoEnvio(cuerpo: unknown): ResultadoEnvio | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const f = (cuerpo as { flushed?: unknown }).flushed;
  if (typeof f !== "object" || f === null) return null;
  const v = f as Record<string, unknown>;
  const n = (k: string): number => (typeof v[k] === "number" ? (v[k] as number) : 0);
  return { sent: n("sent"), accepted: n("accepted"), rejected: n("rejected"), waiting: n("waiting"), failed: v["failed"] === true };
}

export type Pendiente = {
  readonly organizationId: string;
  readonly legalEntityId: string;
  readonly legalName: string;
  readonly taxId: string;
  readonly route: string | null;
  readonly declaredAt: string | null;
};

async function cuerpoJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function comoError(cuerpo: unknown): ErrorApi | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  const detalles = typeof v.details === "object" && v.details !== null ? (v.details as Record<string, unknown>) : {};
  return {
    code: typeof v.code === "string" ? v.code : "",
    message: typeof v.message === "string" ? v.message : "",
    motivo: typeof detalles.reason === "string" ? detalles.reason : null,
    requestId: typeof v.requestId === "string" ? v.requestId : "",
  };
}

async function pedir<T>(ruta: string, init: RequestInit, interpretar: (cuerpo: unknown) => T | null): Promise<Resultado<T>> {
  let res: Response;
  try {
    res = await fetch(ruta, {
      ...init,
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json", ...init.headers },
    });
  } catch {
    return { ok: false, estado: 0, error: null };
  }
  const cuerpo = await cuerpoJson(res);
  if (!res.ok) return { ok: false, estado: res.status, error: comoError(cuerpo) };
  const datos = interpretar(cuerpo);
  // Un 200 con un cuerpo que no es lo esperado no se da por bueno.
  if (datos === null) return { ok: false, estado: res.status, error: null };
  return { ok: true, datos };
}

function comoLogin(cuerpo: unknown): RespuestaLogin | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (v.status === "AUTHENTICATED") return { tipo: "AUTENTICADO" };
  if (v.status === "MFA_REQUIRED" && typeof v.challenge === "string") return { tipo: "MFA", challenge: v.challenge };
  return null;
}

function texto(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function comoResumen(x: unknown): EmpresaResumen | null {
  if (typeof x !== "object" || x === null) return null;
  const v = x as Record<string, unknown>;
  if (typeof v.id !== "string") return null;
  return {
    id: v.id,
    name: texto(v.name),
    planVersion: texto(v.planVersion),
    status: texto(v.status),
    createdAt: texto(v.createdAt),
    shard: typeof v.shard === "string" ? v.shard : null,
    subscriptionState: typeof v.subscriptionState === "string" ? v.subscriptionState : null,
    /*
      `=== true` Y NO UN CAST, y la direccion importa: el backoffice y el
      Worker se despliegan por separado, asi que un admin nuevo puede hablar
      unos minutos con una API que todavia no manda este campo. Ausente ->
      false, que es el lado conservador: una API vieja NUNCA hace que la
      pantalla senale a alguien por no pagar.
    */
    subscriptionUnknown: v.subscriptionUnknown === true,
    planSinSuscripcionViva: v.planSinSuscripcionViva === true,
    prueba: comoPrueba(v.prueba),
  };
}

/** Un estado que no se conoce no se enseña: mejor nada que una etiqueta inventada. */
function comoPrueba(x: unknown): EmpresaResumen["prueba"] {
  if (typeof x !== "object" || x === null) return null;
  const v = x as Record<string, unknown>;
  const estado = ESTADOS_DE_PRUEBA.find((e) => e === v.estado);
  if (estado === undefined || typeof v.endsAt !== "string") return null;
  return { estado, endsAt: v.endsAt };
}

function comoEmpresas(cuerpo: unknown): readonly EmpresaResumen[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).organizations;
  if (!Array.isArray(lista)) return null;
  const salida: EmpresaResumen[] = [];
  for (const x of lista) {
    const fila = comoResumen(x);
    if (fila === null) return null;
    salida.push(fila);
  }
  return salida;
}

/** Un envío ya registrado: el sobre, no su contenido. */
export type Envio = {
  readonly sentAt: string;
  readonly envelopeState: string | null;
  readonly csv: string | null;
  readonly failure: string | null;
  readonly recordCount: number;
};

function comoFicha(cuerpo: unknown): EmpresaFicha | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const org = (cuerpo as Record<string, unknown>).organization;
  const base = comoResumen(org);
  if (base === null) return null;
  const v = org as Record<string, unknown>;

  let issuer: EmpresaFicha["issuer"] = null;
  if (typeof v.issuer === "object" && v.issuer !== null) {
    const e = v.issuer as Record<string, unknown>;
    issuer = { legalName: texto(e.legalName), taxId: texto(e.taxId), confirmed: e.confirmed === true };
  }

  let remision: EmpresaFicha["remision"] = null;
  if (typeof v.remision === "object" && v.remision !== null) {
    const r = v.remision as Record<string, unknown>;
    remision = {
      pending: typeof r.pending === "number" ? r.pending : 0,
      rejected: typeof r.rejected === "number" ? r.rejected : 0,
      incident: r.incident === true,
    };
  }

  const archivosMarcados: ArchivoMarcado[] = [];
  if (Array.isArray(v.archivosMarcados)) {
    for (const x of v.archivosMarcados) {
      if (typeof x !== "object" || x === null) continue;
      const a = x as Record<string, unknown>;
      if (typeof a.fileId !== "string") continue;
      archivosMarcados.push({
        fileId: a.fileId,
        fileType: texto(a.fileType),
        originalName: texto(a.originalName),
        estado: texto(a.estado),
        verifiedAt: typeof a.verifiedAt === "string" ? a.verifiedAt : null,
      });
    }
  }

  const defects: RegistroDefectuoso[] = [];
  if (Array.isArray(v.defects)) {
    for (const x of v.defects) {
      if (typeof x !== "object" || x === null) continue;
      const d = x as Record<string, unknown>;
      if (typeof d.recordId !== "string") continue;
      defects.push({
        recordId: d.recordId,
        numSerie: texto(d.numSerie),
        fechaExpedicion: texto(d.fechaExpedicion),
        status: texto(d.status),
        aeatState: typeof d.aeatState === "string" ? d.aeatState : null,
        errorCode: typeof d.errorCode === "string" ? d.errorCode : null,
        errorDescription: typeof d.errorDescription === "string" ? d.errorDescription : null,
        isSubsanation: d.isSubsanation === true,
      });
    }
  }

  const submissions: Envio[] = [];
  if (Array.isArray(v.submissions)) {
    for (const x of v.submissions) {
      if (typeof x !== "object" || x === null) continue;
      const e = x as Record<string, unknown>;
      submissions.push({
        sentAt: texto(e.sentAt),
        envelopeState: typeof e.envelopeState === "string" ? e.envelopeState : null,
        csv: typeof e.csv === "string" ? e.csv : null,
        failure: typeof e.failure === "string" ? e.failure : null,
        recordCount: typeof e.recordCount === "number" ? e.recordCount : 0,
      });
    }
  }

  return { ...base, issuer, representation: texto(v.representation), remision, submissions, defects, archivosMarcados };
}

function comoPendientes(cuerpo: unknown): readonly Pendiente[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).pending;
  if (!Array.isArray(lista)) return null;
  const salida: Pendiente[] = [];
  for (const x of lista) {
    if (typeof x !== "object" || x === null) return null;
    const v = x as Record<string, unknown>;
    if (typeof v.organizationId !== "string" || typeof v.legalName !== "string") return null;
    salida.push({
      organizationId: v.organizationId,
      legalEntityId: texto(v.legalEntityId),
      legalName: v.legalName,
      taxId: texto(v.taxId),
      route: typeof v.route === "string" ? v.route : null,
      declaredAt: typeof v.declaredAt === "string" ? v.declaredAt : null,
    });
  }
  return salida;
}

/**
 * Un fundador — `OD-0059` D-06. Ids, fechas y estado: lo que hace falta para
 * saber qué llamada falta y cuándo vence. Nada del contenido de las llamadas.
 */
export type Fundador = {
  readonly organizationId: string;
  readonly nombre: string | null;
  readonly estado: string;
  readonly alta: string;
  readonly dia: number;
  readonly llamada1: LlamadaFundador;
  readonly llamada2: LlamadaFundador;
  readonly avisoDeCobro: string | null;
  readonly fechaDeCobro: string | null;
  readonly cobrado: string | null;
};

export type LlamadaFundador = { readonly limite: string; readonly hecha: string | null; readonly por: string | null };

/** `ADR-0024` D-06: las cinco respuestas de José, ya sumadas por la API. */
export type GanadasPerdidas = { readonly ganadas: number; readonly perdidas: number; readonly porcentajeGanadas: number | null };

export type InformeJose = {
  readonly desde: string;
  readonly hasta: string;
  readonly cualificacion: { readonly empezadas: number; readonly completadas: number };
  readonly pasos: {
    readonly apuntados: number;
    readonly hechos: number;
    readonly porcentajeHechos: number | null;
    readonly porPaso: readonly { readonly paso: string; readonly apuntados: number; readonly hechos: number; readonly utiles: number; readonly noUtiles: number }[];
  };
  readonly cierres: { readonly sinCualificar: GanadasPerdidas; readonly cualificadas: GanadasPerdidas; readonly aMano: GanadasPerdidas };
  readonly acierto: readonly ({ readonly porcentaje: number } & GanadasPerdidas)[];
  readonly consejos: readonly { readonly consejo: string; readonly utiles: number; readonly noUtiles: number; readonly otro: number }[];
  readonly recomendaciones: { readonly "1": number; readonly "2": number; readonly "3": number };
};

const esObjeto = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);
const sonEnteros = (o: unknown, claves: readonly string[]): boolean => esObjeto(o) && claves.every((k) => Number.isInteger(o[k]));
const esGp = (o: unknown): boolean =>
  sonEnteros(o, ["ganadas", "perdidas"]) && esObjeto(o) && (o.porcentajeGanadas === null || Number.isInteger(o.porcentajeGanadas));

/** Comprueba la forma entera: una métrica mal leída llevaría a una decisión mal tomada. */
function comoInformeJose(cuerpo: unknown): InformeJose | null {
  const v = esObjeto(cuerpo) ? cuerpo.informe : null;
  if (!esObjeto(v) || typeof v.desde !== "string" || typeof v.hasta !== "string") return null;
  const { cualificacion, pasos, cierres, acierto, consejos, recomendaciones } = v;
  if (!sonEnteros(cualificacion, ["empezadas", "completadas"]) || !sonEnteros(recomendaciones, ["1", "2", "3"])) return null;
  if (!sonEnteros(pasos, ["apuntados", "hechos"]) || !esObjeto(pasos) || !Array.isArray(pasos.porPaso)) return null;
  if (!pasos.porPaso.every((p) => sonEnteros(p, ["apuntados", "hechos", "utiles", "noUtiles"]) && esObjeto(p) && typeof p.paso === "string")) return null;
  if (!esObjeto(cierres) || !esGp(cierres.sinCualificar) || !esGp(cierres.cualificadas) || !esGp(cierres.aMano)) return null;
  if (!Array.isArray(acierto) || !acierto.every((a) => esGp(a) && sonEnteros(a, ["porcentaje"]))) return null;
  if (!Array.isArray(consejos) || !consejos.every((c) => sonEnteros(c, ["utiles", "noUtiles", "otro"]) && esObjeto(c) && typeof c.consejo === "string")) return null;
  return v as unknown as InformeJose;
}

/**
 * Un correo transaccional enviado — `OD-0066`.
 *
 * `recipientMasked` llega ya enmascarado (`j***@dominio.com`): la API no tiene
 * la dirección entera. `null` en los envíos de antes de `global/0026`.
 */
export type CorreoEnviado = {
  readonly kind: string;
  readonly status: string;
  readonly createdAt: string;
  readonly failureReason: string | null;
  readonly provider: string;
  readonly providerMessageId: string | null;
  readonly organizationId: string | null;
  readonly organizationName: string | null;
  readonly recipientRole: string | null;
  readonly recipientMasked: string | null;
};

function comoCorreos(cuerpo: unknown): readonly CorreoEnviado[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as { correos?: unknown }).correos;
  if (!Array.isArray(lista)) return null;
  const correos: CorreoEnviado[] = [];
  for (const x of lista) {
    if (typeof x !== "object" || x === null) return null;
    const v = x as Record<string, unknown>;
    if (typeof v.kind !== "string" || typeof v.status !== "string" || typeof v.createdAt !== "string") return null;
    correos.push({
      kind: v.kind,
      status: v.status,
      createdAt: v.createdAt,
      failureReason: textoONulo(v.failureReason),
      provider: texto(v.provider),
      providerMessageId: textoONulo(v.providerMessageId),
      organizationId: textoONulo(v.organizationId),
      organizationName: textoONulo(v.organizationName),
      recipientRole: textoONulo(v.recipientRole),
      recipientMasked: textoONulo(v.recipientMasked),
    });
  }
  return correos;
}

/** `OD-0064` D-12. Contadores, sin un solo id. `convertidas` va por versión de plan. */
export type ResumenDePruebas = {
  readonly desde: string;
  readonly empezadas: number;
  readonly enCurso: number;
  readonly terminadasSinPagar: number;
  readonly convertidas: Readonly<Record<string, number>>;
};

function comoResumenDePruebas(cuerpo: unknown): ResumenDePruebas | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  const p = v.pruebas;
  if (typeof v.desde !== "string" || typeof p !== "object" || p === null) return null;
  const r = p as Record<string, unknown>;
  if (typeof r.empezadas !== "number" || typeof r.enCurso !== "number" || typeof r.terminadasSinPagar !== "number") return null;
  const convertidas: Record<string, number> = {};
  if (typeof r.convertidas === "object" && r.convertidas !== null) {
    for (const [version, n] of Object.entries(r.convertidas as Record<string, unknown>)) {
      if (typeof n === "number") convertidas[version] = n;
    }
  }
  return { desde: v.desde, empezadas: r.empezadas, enCurso: r.enCurso, terminadasSinPagar: r.terminadasSinPagar, convertidas };
}

export type ListaFundadores = { readonly plazas: number; readonly libres: number; readonly fundadores: readonly Fundador[] };

function textoONulo(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function comoLlamada(x: unknown): LlamadaFundador | null {
  if (typeof x !== "object" || x === null) return null;
  const v = x as Record<string, unknown>;
  if (typeof v.limite !== "string") return null;
  return { limite: v.limite, hecha: textoONulo(v.hecha), por: textoONulo(v.por) };
}

function comoFundador(x: unknown): Fundador | null {
  if (typeof x !== "object" || x === null) return null;
  const v = x as Record<string, unknown>;
  const l1 = comoLlamada(v.llamada1);
  const l2 = comoLlamada(v.llamada2);
  if (typeof v.organizationId !== "string" || typeof v.alta !== "string" || l1 === null || l2 === null) return null;
  return {
    organizationId: v.organizationId,
    nombre: textoONulo(v.nombre),
    estado: texto(v.estado),
    alta: v.alta,
    dia: typeof v.dia === "number" ? v.dia : 0,
    llamada1: l1,
    llamada2: l2,
    avisoDeCobro: textoONulo(v.avisoDeCobro),
    fechaDeCobro: textoONulo(v.fechaDeCobro),
    cobrado: textoONulo(v.cobrado),
  };
}

function comoFundadores(cuerpo: unknown): ListaFundadores | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (typeof v.plazas !== "number" || typeof v.libres !== "number" || !Array.isArray(v.fundadores)) return null;
  const fundadores: Fundador[] = [];
  for (const x of v.fundadores) {
    const f = comoFundador(x);
    if (f === null) return null;
    fundadores.push(f);
  }
  return { plazas: v.plazas, libres: v.libres, fundadores };
}

export const api = {
  login: (email: string, password: string): Promise<Resultado<RespuestaLogin>> =>
    pedir("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }, comoLogin),

  loginMfa: (challenge: string, code: string): Promise<Resultado<RespuestaLogin>> =>
    pedir("/api/v1/auth/login/mfa", { method: "POST", body: JSON.stringify({ challenge, code }) }, comoLogin),

  salir: (): Promise<Resultado<true>> =>
    pedir("/api/v1/auth/logout", { method: "POST", body: JSON.stringify({}) }, () => true as const),

  /** Sólo para saber si hay sesión: el backoffice no usa la organización de quien entra. */
  sesion: (): Promise<Resultado<true>> => pedir("/api/v1/me", { method: "GET" }, () => true as const),

  /** GLOBAL_DB: no registra acceso a los datos de ningún cliente (`OD-0020` E-02). */
  empresas: (): Promise<Resultado<readonly EmpresaResumen[]>> =>
    pedir("/api/v1/internal/organizations", { method: "GET" }, comoEmpresas),

  /** Entra en el shard de esa empresa: queda anotado y el cliente puede verlo (`D-04`). */
  empresa: (id: string): Promise<Resultado<EmpresaFicha>> =>
    pedir(`/api/v1/internal/organizations/${encodeURIComponent(id)}`, { method: "GET" }, comoFicha),

  /**
   * Subsanar un registro (`TDS-FISCAL` Parte 4 §4). Escribe en el shard del
   * cliente, así que deja su acceso anotado igual que la ficha.
   */
  subsanar: (organizationId: string, recordId: string, motivo: string): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/organizations/${encodeURIComponent(organizationId)}/fiscal-records/${encodeURIComponent(recordId)}/subsanation`,
      { method: "POST", body: JSON.stringify({ reason: motivo }) },
      () => true as const,
    ),

  /**
   * «Enviar ahora»: empuja la cola de remisión de esa empresa. No se salta el
   * control de flujo del art. 16.2; si toca esperar, devuelve `waiting`.
   */
  enviarAhora: (organizationId: string): Promise<Resultado<ResultadoEnvio>> =>
    pedir(
      `/api/v1/internal/organizations/${encodeURIComponent(organizationId)}/aeat-submission`,
      { method: "POST", body: JSON.stringify({}) },
      comoEnvio,
    ),

  /**
   * «Comprobar ahora» los archivos de esa empresa — `OD-0044` D-06.
   *
   * No repara nada: anota lo que ve. Un archivo que siga sin cuadrar sigue
   * marcado, así que pulsar esto no es una forma de absolverlo.
   */
  comprobarArchivos: (organizationId: string): Promise<Resultado<ResultadoRepaso>> =>
    pedir(
      `/api/v1/internal/organizations/${encodeURIComponent(organizationId)}/file-integrity`,
      { method: "POST", body: JSON.stringify({}) },
      (cuerpo) => {
        const v = (cuerpo as { sweep?: unknown } | null)?.sweep;
        if (typeof v !== "object" || v === null) return null;
        const s = v as Record<string, unknown>;
        const n = (k: string): number => (typeof s[k] === "number" ? (s[k] as number) : 0);
        return {
          verificados: n("verificados"),
          marcados: n("marcados"),
          sinDeterminar: n("sinDeterminar"),
          habiaAlgo: s.habiaAlgo === true,
        };
      },
    ),

  /**
   * Cerrar deja de operar; reabrir devuelve a ACTIVE. Las dos llevan motivo
   * escrito: dentro de un año es lo único que explicará por qué se hizo.
   */
  cerrar: (organizationId: string, motivo: string): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/organizations/${encodeURIComponent(organizationId)}/close`,
      { method: "POST", body: JSON.stringify({ reason: motivo }) },
      () => true as const,
    ),

  reabrir: (organizationId: string, motivo: string): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/organizations/${encodeURIComponent(organizationId)}/reopen`,
      { method: "POST", body: JSON.stringify({ reason: motivo }) },
      () => true as const,
    ),

  pendientes: (): Promise<Resultado<readonly Pendiente[]>> =>
    pedir("/api/v1/internal/tax-representations", { method: "GET" }, comoPendientes),

  revisar: (organizationId: string, decision: "ACTIVE" | "REJECTED", reason?: MotivoRechazo): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/tax-representations/${encodeURIComponent(organizationId)}/review`,
      { method: "POST", body: JSON.stringify(reason === undefined ? { decision } : { decision, reason }) },
      () => true as const,
    ),
  /**
   * `ADR-0024` D-06: las métricas de José de un periodo (`AAAA-MM-DD`, los dos
   * incluidos; sin periodo, los últimos 30 días). Contadores sin ids.
   */
  metricasJose: (desde: string | null, hasta: string | null): Promise<Resultado<InformeJose>> => {
    const q = new URLSearchParams();
    if (desde !== null) q.set("desde", desde);
    if (hasta !== null) q.set("hasta", hasta);
    const query = q.toString();
    return pedir(`/api/v1/internal/jose-metrics${query === "" ? "" : `?${query}`}`, { method: "GET" }, comoInformeJose);
  },

  /** `OD-0059` D-06. Todo en GLOBAL: no abre el shard de ningún cliente. */
  /** `OD-0064` D-12: los números de la prueba de Business de los últimos 30 días. */
  pruebas: (): Promise<Resultado<ResumenDePruebas>> => pedir("/api/v1/internal/trials", { method: "GET" }, comoResumenDePruebas),

  fundadores: (): Promise<Resultado<ListaFundadores>> => pedir("/api/v1/internal/founders", { method: "GET" }, comoFundadores),

  /**
   * Correos transaccionales — `OD-0066`. GLOBAL_DB: como el listado, no
   * registra acceso a los datos de nadie. Con empresa, los suyos y los de sus
   * usuarios.
   */
  correos: (organizationId: string | null): Promise<Resultado<readonly CorreoEnviado[]>> =>
    pedir(
      organizationId === null
        ? "/api/v1/internal/email-deliveries"
        : `/api/v1/internal/email-deliveries?organizationId=${encodeURIComponent(organizationId)}`,
      { method: "GET" },
      comoCorreos,
    ),

  /**
   * «La llamada N se hizo.» La primera marca gana y no se deshace: marcar es
   * lo que evita el cobro, así que se pulsa cuando la llamada ya ha ocurrido.
   */
  marcarLlamada: (organizationId: string, llamada: 1 | 2): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/founders/${encodeURIComponent(organizationId)}/llamadas/${llamada}`,
      { method: "POST", body: JSON.stringify({}) },
      () => true as const,
    ),
};
